import React, { Component } from 'react';
import { connect } from 'react-redux';
import { debounce } from 'lodash';

import Spinner from '../../components/generics/Spinner';
import { InventoryAuditHeader, InventoryAuditItems, EmptyInventoryAuditItems } from '../../components/inventories/InventoryAuditEdit';
import InventoryAuditAddItem from '../../components/inventories/InventoryAuditAddItem';
import {SCAN_SCANNING, SCAN_LOADING, SCAN_OK, SCAN_ERROR} from '../../components/generics/QRScanner';
import Alert from '../../utils/Alert';

import {
  fetchAudit,
  fetchAuditSuccess,
  fetchAuditFailure,
  addAuditInventory,
  addAuditInventoryFailure,
  addAuditInventorySuccess,
  removeAuditInventory,
  removeAuditInventorySuccess,
  removeAuditInventoryFailure,
} from "../../store/inventories/actions";

import { getInventoryToAudit, finishInventoryAudit } from "../../store/inventories/api";
import { withRouter } from "utils/WithRouter";

export class InventoryAuditContainer extends Component {

  // ---------------------------------------------------------------------------
  // Initialization
  // ---------------------------------------------------------------------------
  constructor(props){
    super(props);

    this.state = {
      lastSave: null,
      code: '',
      inventoryDetail: undefined,
      loadingInventory: false,
      inventoryError: undefined,
      scannedItems: [],
      scannedItem: undefined,
      scannedItemTimes: null,
      scannedItemFormat: null,
      scanResult: undefined,
      scannedSuccess: undefined,
      scanStatus: SCAN_SCANNING,
      loading: false
    };

    this.alertRef = React.createRef();

    this.debounceSearch = debounce(this.searchItemId, 500);
    this.cleanScanResult = debounce(this.cleanMessage, 1500);
  }

  componentDidMount(){
    this.props.fetchAudit(this.props.match.params.id);
  }

  // ---------------------------------------------------------------------------
  // Items handling
  // ---------------------------------------------------------------------------
  onItemIdChange = (event) => {
    this.setState({
      inventoryError: false,
      code: event.target.value,
      inventoryDetail: undefined
    }, () => {
      this.debounceSearch();
    });
  };

  searchItemId = (searchAndAdd, times) => {
    // Hack for backwards compatibility with old labels
    let id = this.state.code;
    var oldLabelFormat = /http:\/\/inventory:8082\/Inventory2\/Action_Pages\/QrScanPage.php?[\W\w]*uniqueid=(\d+)[\W\w]*/
    if (oldLabelFormat.test(id)){
      id = oldLabelFormat.exec(id)[1];
    }

    if(id !== ''){
      if(searchAndAdd){
        this.handleClear();
        this.storeScannedItems(id, null, times);
      } else {
        this.setState({loadingInventory: true});
        getInventoryToAudit(this.props.inventoryAudit.audit.id, id, false).then((r) => {
          let data = r.data;
          if (!data.active){
            data.notRegistered = true;
          }
          this.setState({
            loadingInventory: false,
            inventoryDetail: data,
            inventoryError: false
          });
        }).catch((err) => {
          this.setState({
            loadingInventory: false,
            inventoryError: false,
            inventoryDetail: {id: id, item: {name: 'Not found'},
                              brand: {brand_name: '-'},
                              notRegistered: true}
          });
        });
      }
    }
  };

  handleClear = () => {
    this.setState({
      code: '',
      inventoryError: false,
      inventoryDetail: undefined
    });
  };

  handleAddInventory = (times) => {
    this.storeScannedItems(this.state.code, null, times);
    this.handleClear();
  };

  handleScannedItem = (scan, format, times) => {
    this.setState({
      scanResult: '',
      scanStatus: SCAN_LOADING,
      scannedId: scan,
      scannedItemFormat: format,
      scannedItemTimes: times
    }, this.searchScannedItem);
  };

  searchScannedItem = () => {
    let id = this.state.scannedId;
    var oldLabelFormat = /http:\/\/inventory:8082\/Inventory2\/Action_Pages\/QrScanPage.php?[\W\w]*uniqueid=(\d+)[\W\w]*/
    if (oldLabelFormat.test(id)){
      id = oldLabelFormat.exec(id)[1];
    }

    if(id){
      this.cleanScanResult();
      this.storeScannedItems(id, this.state.scannedItemFormat, this.state.scannedItemTimes);
      this.setState({
        scanResult: `Scanned item #${id}`,
        scanStatus: SCAN_OK
      });
    }
  };

  handleScanError = () => {
    this.setState({scanStatus: SCAN_ERROR, scanResult: 'Cannot scan'});
  };

  cleanMessage = () => {
    this.setState({scanStatus: SCAN_SCANNING, scanResult: ''});
  };

  // ---------------------------------------------------------------------------
  // Audit handling
  // ---------------------------------------------------------------------------
  storeScannedItems = (id, format, times) => {
    this.props.addAuditInventory({
      audit: this.props.inventoryAudit.audit.id,
      scanned_id: id,
      times: times,
      format: format,
    });
  };

  finishAudit = () => {
    this.setState({loading: true});
    let id = this.props.inventoryAudit.audit.id;
    finishInventoryAudit(id).then((data) => {
      this.alertRef.current.sendAlert(
        "Inventory Audit completed",
        "success"
      );
      this.props.history.push(`/inventory/audit/${id}`);
      this.setState({loading: false});
    }, (err) => {
      this.alertRef.current.sendAlert('Cannot finish Inventory Audit');
      this.setState({loading: false});
    });
  };

  onRemoveItem = (id) => {
    let data = {
      audit: this.props.inventoryAudit.audit.id,
      id: id,
    }
    this.props.removeAuditInventory(data);
  };

  reloadAudit = () => {
    this.props.fetchAudit(this.props.inventoryAudit.audit.id);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  render(){
    let scannedItems;
    if(this.props.inventoryAudit.audit){
      scannedItems = this.props.inventoryAudit.audit.scanned_items.map((item)=>{
        if (item.inventory){
          return item.inventory
        }
        return {id: item.scanned_id, item: {name: 'Not found'},
                brand: {brand_name: '-'},
                notRegistered: true}
      })
    } else {
      scannedItems = []
    }
    if (!this.props.inventoryAudit.audit){
      return (
        <div>
          <Alert ref={this.alertRef}/>
          <Spinner />
        </div>
      );
    } else {
      return (
        <div>
          <Alert ref={this.alertRef}/>
          <InventoryAuditHeader audit={this.props.inventoryAudit.audit}
                                finishAudit={this.finishAudit}
                                showFinish={true}
          />

          <div className="card">
            <div className="card-block">
              <InventoryAuditAddItem
                code={this.state.code}
                onItemIdChange={this.onItemIdChange}
                inventory={this.state.inventoryDetail}
                loadingInventory={this.state.loadingInventory}
                itemError={this.state.inventoryError}
                onClear={this.handleClear}
                onAddInventory={this.handleAddInventory}
                onScanItem={this.handleScannedItem}
                onScanError={this.handleScanError}
                scannedItems={scannedItems}
                addScannedItem={this.addScannedItem}
                scanResult={this.state.scanResult}
                scannedSuccess={this.state.scannedSuccess}
                scanStatus={this.state.scanStatus}
                searchAndAddItemId={this.searchItemId}
                scannedItemTimes={this.state.scannedItemTimes}
                multiple_times_allowed={true}
              />
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h5>Inventory Items added</h5>
            </div>
            <div className="card-block m-0 p-0">
              { scannedItems.length === 0 ?
                <EmptyInventoryAuditItems />
              :
                <InventoryAuditItems list={this.props.inventoryAudit.audit.scanned_items}
                                     onRemoveItem={this.onRemoveItem}/>
              }
            </div>
          </div>
          { this.props.inventoryAudit.loading || this.state.loading ? <Spinner /> : null }
        </div>
      )
    }
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    inventoryAudit: state.inventories.inventoryAudit
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    fetchAudit: (id) => {
      dispatch(fetchAudit(id)).payload.then((response) => {
        dispatch(fetchAuditSuccess(response.data))
      }, (error) => {
        dispatch(fetchAuditFailure(error));
      });
    },
    addAuditInventory: (data) => {
      dispatch(addAuditInventory(data)).payload.then((response) => {
        dispatch(addAuditInventorySuccess(response.data))
      }, (error) => {
        dispatch(addAuditInventoryFailure(error))
      });
    },
    removeAuditInventory: (data) => {
      dispatch(removeAuditInventory(data)).payload.then((response) => {
        dispatch(removeAuditInventorySuccess(response.data))
      }, (error) => {
        dispatch(removeAuditInventoryFailure(error))
      });
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(InventoryAuditContainer));
